import React, { useState, useEffect } from "react";
import axios from "axios";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import SupplierAdd from "./SupplierAdd";
import SupplierEdit from "./SupplierEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";

const SupplierIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const currentUser = auth?.user?.user || auth?.user || {};
  const token = auth?.user?.access || auth?.user?.token || auth?.token;
  const isAdmin = Boolean(
    currentUser?.is_superuser ||
      currentUser?.is_staff ||
      auth?.user?.user?.is_superuser
  );
  const [selectedBranch, setSelectedBranch] = useState(
    auth?.user?.selected_branch ? String(auth.user.selected_branch) : ""
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showTransfers, setShowTransfers] = useState(true);

  const [expandedTransferBranches, setExpandedTransferBranches] = useState({});
  const [deletingTransferGroupKey, setDeletingTransferGroupKey] = useState("");
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [openGroupDeleteConfirm, setOpenGroupDeleteConfirm] = useState(false);

  // Fetch suppliers with search filtering and pagination.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["supplier", searchTerm, page, rowsPerPage, selectedBranch],
    `shop/suppliers/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}${selectedBranch ? `&shop=${selectedBranch}` : ""}`,
    token
  );
  const { data: branchData } = useFetchObjects(
    ["supplier-transfer-branches"],
    "shop/shop/?page_size=all&include_all=1",
    token
  );
  const { data: stockTransfersData, isLoading: isStockTransfersLoading } = useFetchObjects(
    ["supplier-stock-transfers", selectedBranch],
    `shop/stock-transfers/?page_size=10000${selectedBranch ? `&customer_branch=${selectedBranch}` : ""}`,
    token
  );
  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, selectedBranch, refetch]);

  const { handleDelete, ConfirmDialog, DeleteMessageComponent } = useDelete("shop/suppliers", token, refetch);
  const { handleDelete: handleTransferDelete, ConfirmDialog: ConfirmTransferDelete, DeleteMessageComponent: TransferDeleteMessage } = useDelete("shop/stock-transfers", token, refetch);

  const deleteTransferBranchGroup = (group) => {
    setGroupToDelete(group);
    setOpenGroupDeleteConfirm(true);
  };

  const handleConfirmGroupDelete = async () => {
    if (!groupToDelete) return;
    const transferIds = groupToDelete?.items
      ?.map((item) => item?.sourceTransfer?.id)
      .filter(Boolean);
    if (!transferIds?.length) return;

    setOpenGroupDeleteConfirm(false);
    setDeletingTransferGroupKey(groupToDelete.rowKey);
    try {
      const headers = auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` };
      await Promise.allSettled(
        transferIds.map((id) =>
          axios.delete(`${process.env.REACT_APP_API_BASE_URL}/shop/stock-transfers/${id}/`, {
            headers,
          })
        )
      );
      refetch?.();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to delete stock transfer records.");
    } finally {
      setDeletingTransferGroupKey("");
      setGroupToDelete(null);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const suppliers = data && data.results ? data.results : data || [];
  const branches = branchData?.results || branchData || [];
  const branchNameById = branches.reduce((map, branch) => {
    map[String(branch.id)] = branch.name;
    return map;
  }, {});

  const stockTransfers = (stockTransfersData?.results || stockTransfersData || []).filter((transfer) =>
    Boolean(transfer?.show_payment_button)
  );

  const stockTransferSupplierRows = stockTransfers.map((transfer) => {
    const fromBranchName =
      transfer?.supplier_branch_details?.name ||
      branchNameById[String(transfer?.supplier_branch)] ||
      "--";
    const toBranchName =
      transfer?.customer_branch_details?.name ||
      branchNameById[String(transfer?.customer_branch)] ||
      "--";

    return {
      id: fromBranchName,
      rowKey: `transfer-${transfer.id}`,
      recordType: "stock-transfer",
      name: fromBranchName,
      email: "null",
      contact: "null",
      address: toBranchName,
      shop: transfer?.supplier_branch || "",
      shop_details: {
        id: transfer?.supplier_branch || "",
        name: fromBranchName,
      },
      branch_display_name: fromBranchName,
      sourceTransfer: transfer,
    };
  });

  const supplierRows = suppliers.map((supplier) => ({
    ...supplier,
    recordType: "supplier",
  }));

  const groupedTransferRows = stockTransferSupplierRows.reduce((groups, row) => {
    const key = row.name || "--";
    if (!groups[key]) {
      groups[key] = {
        id: key,
        rowKey: `transfer-group-${key}`,
        recordType: "stock-transfer-group",
        name: key, // From Branch Name
        email: "null",
        contact: "null",
        address: row.address, // To Branch Name
        shop: row.shop || "",
        shop_details: row.shop_details || { name: "--" },
        branch_display_name: key,
        items: [],
      };
    }
    groups[key].items.push(row);
    return groups;
  }, {});

  const mergedRows = [...supplierRows, ...Object.values(groupedTransferRows)];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredRows = normalizedSearch
    ? mergedRows.filter((row) =>
        [
          row?.name,
          row?.email,
          row?.contact,
          row?.address,
          row?.shop_details?.name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch))
      )
    : mergedRows;

  const pagedRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalCount = filteredRows.length;


  if (isError) {
    return <ErrorPage message={t("supplier.failedToLoadSuppliers")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("supplier.supplierList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("supplier.addNewSupplier")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("supplier.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>
      {/* Search and Filter Field */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder={t("supplier.searchSuppliers")}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="branch-select-label">{t("supplier.shop")}</InputLabel>
            <Select
              labelId="branch-select-label"
              value={selectedBranch}
              onChange={handleBranchChange}
              label={t("supplier.shop")}
            >
              <MenuItem value="">
                <em>{t("global.allShops", "All Shops")}</em>
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={String(branch.id)}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {/* Suppliers Table */}
      <TableContainer
        component={Paper}
        sx={{ mt: 2, mb: 0, maxHeight: 540, overflowY: "auto" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="supplier table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>{t("supplier.id")}</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                {t("supplier.name")}
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                {t("supplier.email")}
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                {t("supplier.contact")}
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                {t("supplier.address")}
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                {t("supplier.shop")}
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                {t("supplier.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading || isStockTransfersLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Name",
                      "Email",
                      "Contact",
                      "Address",
                      "Shop",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : pagedRows.map((row, index) => {
                  const isTransferGroup = row.recordType === "stock-transfer-group";
                  return (
                    <React.Fragment key={row.rowKey || row.id}>
                      <TableRow
                        hover
                        sx={
                          isTransferGroup
                            ? {
                                backgroundColor: "#f6faff",
                                "& td": {
                                  borderBottom: Boolean(expandedTransferBranches[row.rowKey])
                                    ? "none"
                                    : undefined,
                                },
                              }
                            : undefined
                        }
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {isTransferGroup ? (
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedTransferBranches((prev) => ({
                                  ...prev,
                                  [row.rowKey]: !prev[row.rowKey],
                                }))
                              }
                              sx={{ mr: 1, p: 0, color: "primary.main" }}
                            >
                              {expandedTransferBranches[row.rowKey] ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          ) : null}
                          {(page - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", color: "primary.main" }}>
                          {row.name}
                        </TableCell>
                        <TableCell>{row.email || "null"}</TableCell>
                        <TableCell>{row.contact || "null"}</TableCell>
                        <TableCell>{row.address || "null"}</TableCell>
                        <TableCell>
                          {isTransferGroup
                            ? branchNameById[selectedBranch] || "All Branches"
                            : row.shop_name ||
                              (row.shop && row.shop.name) ||
                              row.shop ||
                              "No Shop"}
                        </TableCell>
                        <TableCell>
                          {isTransferGroup ? (
                            <>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => console.log("Edit transfers group", row.id)}
                              >
                                {t("supplier.edit", "EDIT")}
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={deletingTransferGroupKey === row.rowKey}
                                onClick={() => deleteTransferBranchGroup(row)}
                              >
                                {deletingTransferGroupKey === row.rowKey
                                  ? t("stock.deleting", "Deleting...")
                                  : t("supplier.delete", "Delete")}
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => {
                                  setEditData(row);
                                  setOpenEdit(true);
                                }}
                              >
                                {t("supplier.edit")}
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleDelete(row.id)}
                              >
                                {t("supplier.delete")}
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      {isTransferGroup && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, px: 0 }}>
                            <Collapse
                              in={Boolean(expandedTransferBranches[row.rowKey])}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ p: 2, bgcolor: "#fbfdff" }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: "white" }}>
                                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>ID</TableCell>
                                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>From</TableCell>
                                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>To</TableCell>
                                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Product</TableCell>
                                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Qty</TableCell>
                                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Date</TableCell>
                                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.items.map((item) => (
                                      <TableRow key={item.rowKey}>
                                        <TableCell>{item.sourceTransfer?.id}</TableCell>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.address}</TableCell>
                                        <TableCell>{item.sourceTransfer?.product_details?.name}</TableCell>
                                        <TableCell>{item.sourceTransfer?.transfer_quantity}</TableCell>
                                        <TableCell>{item.sourceTransfer?.transfer_date}</TableCell>
                                        <TableCell>
                                          {item.sourceTransfer?.status_display || item.sourceTransfer?.status}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
          </TableBody>

        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("supplier.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      {openAdd && (
        <SupplierAdd
          open={openAdd}
          onClose={() => {
            setOpenAdd(false);
            refetch();
          }}
          token={token}
          initialShop={selectedBranch}
        />
      )}
      {openEdit && (
        <SupplierEdit
          open={openEdit}
          onClose={() => {
            setOpenEdit(false);
            refetch();
          }}
          editData={editData}
          token={token}
        />
      )}
      <ConfirmDialog />
      <DeleteMessageComponent />
      <ConfirmTransferDelete />
      <TransferDeleteMessage />

      {/* Group Delete Confirmation Dialog */}
      <Dialog
        open={openGroupDeleteConfirm}
        onClose={() => setOpenGroupDeleteConfirm(false)}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WarningAmberIcon color="error" sx={{ fontSize: "5rem" }} />
            <Typography variant="h5" fontWeight={"bold"}>
              Confirm Deletion
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this item? This action cannot be undone.
          </Typography>
          <strong>
            Delete stock transfer records for {groupToDelete?.name}?
          </strong>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => setOpenGroupDeleteConfirm(false)}
            color="primary"
            sx={{ px: 4, mr: 2 }}
          >
            CANCEL
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmGroupDelete}
            color="error"
            sx={{ px: 4 }}
          >
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierIndex;
